<template>
    <div :data-component="$options.name">
        <div
            class="relative flex items-center justify-start h-64 p-4 transition-shadow cursor-pointer rounded-10 bg-color-neutral-white/50 hover:bg-white"
            :class="{ 'mb-8': hasWeatherWarning }"
            data-jest="weather-box"
            @click="toggleOpen"
        >
            <weather-icon
                v-if="weatherServiceData.currentWeather.iconName"
                class="flex-shrink-0 w-56 h-56 p-4 mr-4 bg-color-neutral-white rounded-normal"
                :iconName="weatherServiceData.currentWeather.iconName"
            ></weather-icon>
            <div class="mx-4 text-h3 font-bold-headline">
                {{ weatherServiceData.currentWeather.interpolatedTemperature }}°
            </div>
            <div
                class="font-bold text-text"
                :class="{
                    'ml-4': hasWeatherWarning && highestWeatherWarning,
                }"
            >
                {{ weatherServiceData.currentWeather.weatherCondition }}
                <p
                    v-if="hasWeatherWarning && highestWeatherWarning"
                    class="flex text-caption"
                    data-jest="weather-warning"
                >
                    Unwetterwarnung
                    <warning
                        v-if="highestWeatherWarning.warningLevel === 3 || highestWeatherWarning.warningLevel === 4"
                        class="w-20 h-20 ml-8"
                    />
                    <warning-light
                        v-if="highestWeatherWarning.warningLevel === 2"
                        class="w-20 h-20 ml-8"
                    />
                    <warning-lighter
                        v-if="highestWeatherWarning.warningLevel === 1"
                        class="w-20 h-20 ml-8"
                    />
                </p>
            </div>
            <ms-chevron class="w-40 h-40 p-8 ml-auto -rotate-90"></ms-chevron>
        </div>
        <cs-modal
            :visible="isOpen"
            :headerText="weatherServiceData.title"
            :contentRendered="true"
            @update:visible="toggleOpen"
        >
            <div
                class="flex flex-col gap-16 overflow-y-auto overflow-x-hidden h-full screen-m:max-h-[calc(100vh_-_268px)]"
            >
                <div
                    class="relative flex flex-col items-start mt-16 screen-m:!mt-8 screen-m:items-center screen-m:flex-row"
                >
                    <div class="flex items-center mb-8 screen-m:mb-0">
                        <weather-icon
                            v-if="weatherServiceData.currentWeather.iconName"
                            class="w-48 h-48"
                            :iconName="weatherServiceData.currentWeather.iconName"
                        ></weather-icon>
                        <div
                            class="flex flex-col items-start justify-center pr-24 ml-16 screen-m:border-r screen-m:border-solid"
                        >
                            <h3 class="font-bold-headline text-h3">
                                {{ weatherServiceData.currentWeather.interpolatedTemperature }}°
                            </h3>
                            <div class="font-bold text-text">
                                {{ weatherServiceData.currentWeather.weatherCondition }}
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="hasDynamicTitle"
                        class="screen-m:ml-24 text-text"
                        data-jest="dynamic-title"
                    >
                        {{ weatherServiceData.currentWeather.dynamicTitle }}
                    </div>
                </div>
                <div
                    v-if="hasWeatherWarning && highestWeatherWarning"
                    class="mt-16 screen-m:mt-24"
                    data-jest="weather-warning-extended"
                >
                    <div
                        :key="highestWeatherWarning.text"
                        :class="{
                            'bg-color-alert-negative-25 border-color-alert-negative-25':
                                highestWeatherWarning.warningLevel === 3 || highestWeatherWarning.warningLevel === 4,
                            'bg-color-rating-mediumorange-25 border-color-rating-mediumorange-25':
                                highestWeatherWarning.warningLevel === 2,
                            'bg-color-rating-lightorange-25 border-color-rating-lightorange-25':
                                highestWeatherWarning.warningLevel === 1,
                        }"
                        class="p-6 mt-24 border border-solid first:mt-0 border-col rounded-10"
                        data-jest="weatherwarning-text"
                    >
                        <div class="flex items-center mb-4 mr-4 font-bold">
                            <warning
                                v-if="
                                    highestWeatherWarning.warningLevel === 3 || highestWeatherWarning.warningLevel === 4
                                "
                                class="w-24 h-24"
                            />
                            <warning-light
                                v-if="highestWeatherWarning.warningLevel === 2"
                                class="w-24 h-24"
                            />
                            <warning-lighter
                                v-if="highestWeatherWarning.warningLevel === 1"
                                class="w-24 h-24"
                            />
                            Unwetterwarnung - Warnstufe {{ highestWeatherWarning.warningLevel }}
                        </div>

                        {{ highestWeatherWarning.text }}
                    </div>
                </div>
                <div class="grid w-full grid-cols-2 gap-16 mt-16">
                    <div
                        v-for="weatherConditonTimeOfDay in weatherServiceData.weatherConditonTimeOfDay"
                        :key="weatherConditonTimeOfDay.headline"
                        class="flex flex-col items-center justify-center p-16 border border-solid rounded-10"
                    >
                        <div class="mb-8 font-bold text-text">{{ weatherConditonTimeOfDay.headline }}</div>
                        <weather-icon
                            v-if="weatherConditonTimeOfDay.iconName"
                            class="w-40 h-40 mb-16"
                            :iconName="weatherConditonTimeOfDay.iconName"
                        />
                        <div class="mb-8 text-h3 font-bold-headline">{{ weatherConditonTimeOfDay.temperature }}°</div>
                        <div class="flex flex-col items-center justify-center mb-4 screen-m:flex-row">
                            <div class="mb-8 text-dovegray text-text screen-m:border-r screen-m:pr-8 screen-m:mb-0">
                                {{ weatherConditonTimeOfDay.weatherCondition }}
                            </div>
                            <div
                                v-if="weatherConditonTimeOfDay.rainProbability > 0"
                                class="flex items-center screen-m:ml-8"
                            >
                                <ms-raindrop class="w-20 h-20 mr-4 text-dovegray" />
                                <div class="text-text text-dovegray">
                                    {{ weatherConditonTimeOfDay.rainProbability }}%
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <cs-link
                    :href="weatherServiceData.weatherUrl"
                    target="_blank"
                    @click="trackWeatherClick"
                >
                    Zur Wettervorhersage
                </cs-link>
            </div>
        </cs-modal>
    </div>
</template>
<script lang="ts" src="./WeatherModule.ts" />
<style scoped lang="scss" src="./WeatherModule.scss" />
